import { InputLabel, TextField, Tooltip } from "@mui/material";
import NumberFormat from "react-number-format";
import React, { useEffect, useRef } from "react";
import "./components.css";
import CustomTooltip from "./CustomTooltip";
import {useTranslation} from "react-i18next";

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(props, ref) {
  const { onChange, min, max, prefix, pageKey, label, ...other } = props;
  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      isAllowed={(values) => {
        const { formattedValue, floatValue } = values;
        return (
          formattedValue === "" || 
          (floatValue >= min && floatValue <= max) || 
          formattedValue === "-"
        );
      }}
      
      onValueChange={(values) => {
        let value = values.value;

        // Check if pageKey is LoanDetails and prefix is $, round the value to nearest integer
        // if (pageKey === "LoanDetails" && prefix === "$" && (label !== "DownPaymentAmount") && value) {
        //   const regex = /^\d+(\.\d{2})?$/; // Matches a number with up to 2 decimal places
        //   if (regex.test(value)) {
        //   value = Math.round(values.floatValue).toString();
        //   }
        // }
        if (value !== props.value) {
          onChange({
            target: {
              name: props.name,
              value,
            },
          });
        }
      }}
      decimalScale={2}
      fixedDecimalScale={false}
      thousandSeparator
      isNumericString
      allowNegative={min !== 0}
    />
  );
});

export default function NumberComponent(props) {
  const {t} = useTranslation();
  const {
    label,
    onChange,
    required,
    name,
    value,
    minimum,
    maximum,
    tooltip,
    prefix,
    error,
    helperText,
    disabled,
    pageKey,
    pattern
  } = props;
  let min, max;

  if (minimum !== undefined && maximum !== undefined) {
    min = parseInt(minimum);
    max = parseInt(maximum);
  }

  const inputRef = useRef(null);

  useEffect(() => {
    const handleWheel = (e) => {
      e.preventDefault();
    };

    const input = inputRef.current;
    input?.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      input?.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const blockInvalidChar = (e) =>
    ["e", "E"].includes(e.key) && e.preventDefault();

  const blockDot = (e) => ["e", "E", "."].includes(e.key) && e.preventDefault();
  const handleOnChange = (e) => {
    let val = e.target.value;

    // If the input is empty, allow the empty value
    if (val === "") {
      onChange(e);  // Allow empty value
      return;
    }

    const filteredValue = val.replace(/[^0-9.-]/g, "");

    // Parse the filtered value to check if it's a valid number
    const parsedValue = parseFloat(filteredValue);

    // If the value is a valid number, check the min/max range
    if (!isNaN(parsedValue)) {
      // If the value is within the allowed range, update the state
      if ((min === undefined || parsedValue >= min) && (max === undefined || parsedValue <= max)) {
        // Update only if the parsed value is valid and within range
        onChange({
          ...e,
          target: {
            ...e.target,
            name: props.name,
            value: filteredValue, // Set filtered value
          },
        });
      }
    }
  };

  

  const tooltipMsg = () => {
    if (tooltip) {
      return t(tooltip);
    } else if (min !== undefined && max !== undefined) {
      return `(${t('Ex')}:- ${prefix === "$" ? formatter.format(min) : min} ${t('to')} ${prefix === "$" ? formatter.format(max) : max
        } ${t('range')})`;
    }
  };
  
  return (
    <div
      className="flexAlignColumnClassName"
      style={{ marginBottom: helperText ? "10px" : "0px" }}
      onClick={(e) => { e.preventDefault() }}
    >
      <InputLabel
        htmlFor="outlined-adornment-amount"
        required={required}
        className="labelClassName alignRowClassName flex-start"
      >
        {t(label)}
        {tooltipMsg() && (
          <CustomTooltip title={tooltipMsg()}>
            <span className="tooltipInfoIcon">i</span>
          </CustomTooltip>
        )}
      </InputLabel>
      <TextField
        inputRef={inputRef}
        data-testid="textfield"
        type={prefix === "$" ? null : "text"}
        inputProps={{
          min: min,
          max: max,
          prefix: prefix,
          pageKey: pageKey,
          label: label,
          pattern: pattern,
          inputMode: prefix === "$" || (min !== undefined || max !== undefined) && "numeric"
        }}
        name={name}
        autoComplete="off"
        id="outlined-adornment-amount"
        className="textFieldInputComponent"
        onKeyDown={prefix === "$" ? blockInvalidChar : blockDot}
        value={!isNaN(value) ? value.toString() : ""}
        onAnimationStart={(e) => props.onLoad(e)}
        onChange={(e) => handleOnChange(e)}
        required={required}
        disabled={disabled}
        InputProps={{
          inputComponent: prefix === "$" && NumberFormatCustom,
          classes: {
            root: error
              ? "outlinedErrorInputRoot menuItemOptionTag"
              : "outlinedInputRoot menuItemOptionTag",
            focused: error
              ? "outlinedErrorInputFocused"
              : "outlinedInputFocused",
            notchedOutline: "outlinedInput",
          },
          startAdornment: prefix && (
            <div style={{ paddingRight: 5 }}>{prefix}</div>
          ),
        }}
        FormHelperTextProps={{
          classes: {
            root: "helperTextClassName",
          },
        }}
        helperText={t(helperText)}
        error={t(error)}
      />
    </div>
  );
}